// SpecialityCompetitionChart.js

import React, { useState, useRef, useEffect } from 'react';
import Plot from 'react-plotly.js';
import competitionData from '../data/competitionData.json';
import '../css/SpecialityCompetitionChart.css';
import { FaChartLine, FaSearch, FaInfoCircle, FaTimes, FaChevronDown } from 'react-icons/fa';
import { chartColors, defaultLayout, plotConfig } from '../config/plotlyConfig';

const SpecialityCompetitionChart = () => {
  // Create options for dropdown and sort alphabetically
  const specialtyOptions = Object.keys(competitionData)
    .sort((a, b) => a.localeCompare(b))
    .map((specialty) => ({
      label: specialty,
      value: specialty,
    }));

  // Set initial selected specialties
  const initialSpecialties = [
    specialtyOptions.find(opt => opt.value === "General Practice ST1") || specialtyOptions[0],
    specialtyOptions.find(opt => opt.value === "Core Surgical Training CT1") || specialtyOptions[1],
    specialtyOptions.find(opt => opt.value === "Internal Medicine Training CT1") || specialtyOptions[2],
  ];
  
  const [selectedSpecialties, setSelectedSpecialties] = useState(initialSpecialties);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const optionsContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = specialtyOptions.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle selection of specialty
  const toggleSelection = (option) => {
    // Store current scroll position before state update
    if (optionsContainerRef.current) {
      scrollPositionRef.current = optionsContainerRef.current.scrollTop;
    }
    
    const isSelected = selectedSpecialties.some(item => item.value === option.value);
    if (isSelected) {
      setSelectedSpecialties(selectedSpecialties.filter(item => item.value !== option.value));
    } else {
      setSelectedSpecialties([...selectedSpecialties, option]);
    }
    
    // We'll restore the scroll position in useEffect
  };

  // Restore scroll position after selection
  useEffect(() => {
    if (isDropdownOpen && optionsContainerRef.current) {
      optionsContainerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [selectedSpecialties, isDropdownOpen]);

  // Clear a specific selection
  const clearSelection = (option, e) => {
    e.stopPropagation();
    setSelectedSpecialties(selectedSpecialties.filter(item => item.value !== option.value));
  };

  // Clear all selections
  const clearAllSelections = (e) => {
    e.stopPropagation();
    setSelectedSpecialties([]);
  };

  // Custom dropdown component
  const CustomDropdown = () => (
    <div className="custom-dropdown-wrapper" ref={dropdownRef}>
      {/* Dropdown header - shows selected items and triggers dropdown */}
      <div 
        className="custom-dropdown-header"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="selected-options">
          {selectedSpecialties.length === 0 ? (
            <span className="placeholder">Select specialties...</span>
          ) : (
            <>
              {selectedSpecialties.map(option => (
                <div key={option.value} className="selected-chip">
                  {option.label}
                  <button 
                    className="clear-chip"
                    onClick={(e) => clearSelection(option, e)}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="dropdown-controls">
          {selectedSpecialties.length > 0 && (
            <button 
              className="clear-all-btn"
              onClick={clearAllSelections}
              title="Clear all"
            >
              <FaTimes />
            </button>
          )}
          <span className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`}>
            <FaChevronDown />
          </span>
        </div>
      </div>
      
      {/* Dropdown content - only visible when dropdown is open */}
      {isDropdownOpen && (
        <div className="custom-dropdown-content">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search specialties..."
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              onFocus={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(true);
              }}
              onBlur={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="search-input"
              autoComplete="off"
              autoFocus
            />
          </div>
          <div className="options-container" ref={optionsContainerRef}>
            {filteredOptions.length === 0 ? (
              <div className="no-options">No matching specialties found</div>
            ) : (
              filteredOptions.map(option => {
                const isSelected = selectedSpecialties.some(item => item.value === option.value);
                return (
                  <div
                    key={option.value}
                    className={`option-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleSelection(option)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="option-checkbox"
                    />
                    <span className="option-label">{option.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Prepare the Plotly data based on selected specialties
  const plotData = selectedSpecialties.map((specialtyOption, index) => {
    const specialty = specialtyOption.value;
    const specialtyData = competitionData[specialty];
    const years = Object.keys(specialtyData).sort((a, b) => parseFloat(a) - parseFloat(b));
    const applications = years.map((year) => specialtyData[year]['Applications']);
    const posts = years.map((year) => specialtyData[year]['Posts']);
    const competitionRatios = years.map((year) => specialtyData[year]['Competition ratio']);
    const labels = years.map(
      (year, idx) => `Applications: ${applications[idx]}, Posts: ${posts[idx]}`
    );
    
    return {
      x: years.map((year) => parseFloat(year)),
      y: competitionRatios,
      type: 'scatter',
      mode: 'lines+markers',
      name: specialty,
      text: labels,
      line: { 
        width: 3,
        color: chartColors[index % chartColors.length],
        shape: 'spline'
      },
      marker: { 
        size: 8,
        color: chartColors[index % chartColors.length]
      },
      hovertemplate:
        `<b>${specialty}</b><br>` +
        'Year: %{x}<br>' +
        'Competition Ratio: %{y:.2f}<br>' +
        '%{text}<extra></extra>',
    };
  });

  // Find the maximum competition ratio among the selected specialties
  let maxY = 7;
  selectedSpecialties.forEach((specialtyOption) => {
    const specialty = specialtyOption.value;
    const specialtyData = competitionData[specialty];
    const competitionRatios = Object.values(specialtyData).map(
      (entry) => entry['Competition ratio']
    );
    const specialtyMax = Math.max(...competitionRatios) * 1.1;
    if (specialtyMax > maxY) {
      maxY = specialtyMax;
    }
  });

  // Use the imported layout and add dynamic properties
  const layout = {
    ...defaultLayout,
    yaxis: {
      ...defaultLayout.yaxis,
      range: [0, maxY]
    },
    shapes: [{
      type: 'line',
      x0: plotData.length > 0 ? Math.min(...plotData[0].x) : 2021,
      y0: 1,
      x1: plotData.length > 0 ? Math.max(...plotData[0].x) : 2025,
      y1: 1,
      line: {
        color: '#718096',
        width: 1,
        dash: 'dash',
      }
    }]
  };

  return (
    <div className="competition-container">
      <div className="competition-header">
        <div className="competition-title">
          <FaChartLine className="competition-icon" />
          <h1>Competition Ratios</h1>
        </div>
        <p className="competition-subtitle">
          Competition ratios show how many applications are received per available position. For example, a ratio of 4:1 means there are 4 applications for every 1 available post in that specialty.
        </p>
      </div>
      
      <div className="chart-container">
        <div className="selector-panel">
          <div className="selector-header">
            <label htmlFor="specialty-select">Select specialties to compare:</label>
            <div className="tooltip-wrapper">
              <FaInfoCircle className="info-icon" />
              <span className="tooltip-text">Competition ratio = Number of applications / Number of available posts</span>
            </div>
          </div>
          
          <div className="multiselect-wrapper">
            <CustomDropdown />
          </div>
        </div>

        {selectedSpecialties.length === 0 ? (
          <div className="empty-selection">
            <FaChartLine className="empty-icon" />
            <p>Please select at least one specialty to display the chart.</p>
            <button 
              className="select-popular-btn"
              onClick={() => setSelectedSpecialties([
                specialtyOptions.find(opt => opt.value === "General Practice ST1") || specialtyOptions[0]
              ])}
            >
              Select Popular Specialty
            </button>
          </div>
        ) : (
          <div className="plot-container">
            <Plot
              data={plotData}
              layout={layout}
              config={plotConfig}
              style={{ width: '100%', height: '600px' }}
              useResizeHandler={true}
              className="responsive-plot"
            />
          </div>
        )}
        
        <div className="chart-footer">
          <p>Data source: HEE Competition Ratios | Last updated: Sept 2025</p>
        </div>
      </div>
    </div>
  );
};

export default SpecialityCompetitionChart;